<template>
  <template v-if="auditorType === 'image'">
    <v-layout :full-height="true" style="z-index: 9;" class="flex-column">
      <div
        ref="modalDisplay"
        class="display"
        :tabindex="selectedAnnotations.length > 0"
      >
        <BaseSidebar :collapsible="true">
          <div class="annotation-editor-modal__sidebar">
            <JsonRawViewer
              v-model:displayLabelTags="displayLabelTags"
              v-model:selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
              v-model:selectedLabels="annotationDisplaySettings.displayLabels.labels"
              v-model:selectedLabel="selectedLabel"
              v-model:displayProjection="displayProjection"
              v-model:colorAnnotationsBy="annotationDisplaySettings.colorAnnotationsBy"
              v-model:showTypes="showTypes"
              :labels="labels"
              :imageObj="currentSample.imageObj"
              :annotationSets="annotationSets"
              :fixedAnnotationSet="Boolean(annotationSetToEdit)"
              :groupedAnnotations="groupedAnnotations"
              :selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
              :hiddenAnnotationIdentifiers="hiddenAnnotationIdentifiers"
              :isEditing="mode === 'edit'"
              :hideAddTopics="mode !== 'edit'"
              :hideAnnotationList="drawMode === 'sam2'"
              @remove-annotation="handleAnnotationDeleteClicked"
              @remove-annotation-type="handleAnnotationTypeDelete"
              @select-annotations="selectAnnotation"
              @toggle-annotation-selection="toggleAnnotationSelection"
              @toggle-annotation-visibility="toggleAnnotationVisibility"
              @toggle-annotation-type-visibility="toggleAnnotationTypeVisibility"
              @join-annotations="handleJoinFirst"
              @label-clicked="editorStore.relabelSelectedAnnotations"
              @edit-for-annotation-set="handleStartEditingAnnotations"
              @new-color="(params)=>updateLabelColor(params, { dataset_id: currentSample?.imageObj?.dataset_id })"
              @add-annotation-sets="openAddAnnotationSetsModal"
              @add-labels="openAddLabelsModal"
            >
              <template #utilities-slot>
                <AddVisualizationMenu @layout-selected="handleAddVisualization" />
                <SelectLayoutMenu @layout-selected="handleLayoutSelected" />
              </template>
            </JsonRawViewer>
            <template v-if="currentSample.imageObj && groups.length > 0">
              <div class="image-group">
                <label class="image-group__label">Group</label>
                <ImageGroupSelector :imageGroupId="imageGroupId" :groups="groups" @change="handleImageGroupChanged" />
              </div>
            </template>
          </div>
        </BaseSidebar>
        <div class="visualization">
          <template v-if="mode === 'edit'">
            <AnnotationToolAGTGRibbon
              v-if="drawMode === 'sam2'"
              ref="annotationToolAGTGRibbon"
              @submit="handleSequenceSubmit"
            />
            <AnnotationToolSequenceRibbon
              v-else-if="internalEditAsSequence || isSequence"
              ref="annotationToolSequenceRibbon"
              :canUndo="prevInternalAnnotations !== null"
              :hasSelection="selectedAnnotationIdentifiers.length > 0"
              :annotationDisplaySettings="annotationDisplaySettings"
              :isAuditMode="isAuditMode"
              :frameImageObj="currentSample.imageObj"
              :reviewSettings="reviewSettings"
              :selectedAnnotations="selectedAnnotations"
              :hasCopiedAnnotations="hasCopiedAnnotations"
              :keyFrameEnabled="keyFrameEnabled"
              :editMode="drawMode"
              @undo="handleUndo"
              @revert-changes="handleRevertChanges"
              @show-annotation-info="showAnnotationInfo = true"
              @submit="handleSequenceSubmit"
              @copy="handleCopyAnnotations"
              @paste="handlePasteAnnotations"
              @bulk-del-ann="handleDeleteAnnotationBulk"
              @join-first="handleJoinFirst"
              @join-last="handleJoinLast"
            />
            <AnnotationToolRibbon
              v-else
              :canUndo="prevInternalAnnotations !== null"
              :hasSelection="selectedAnnotationIdentifiers.length > 0"
              :selectionCount="selectedAnnotationIdentifiers.length"
              :annotationDisplaySettings="annotationDisplaySettings"
              :isAuditMode="isAuditMode"
              @undo="handleUndo"
              @revert-changes="handleRevertChanges"
              @show-annotation-info="showAnnotationInfo = true"
              @submit="handleSubmit"
              @bulk-del-ann="handleDeleteAnnotationBulk"
              @join-first="handleJoinFirst"
              @join-last="handleJoinLast"
            />
          </template>
          <VisualizationDisplays
            ref="visualizationDisplays"
            v-model:layout="layout"
            :loading="isInitializingAnimation || ((internalEditAsSequence || isSequence) && isLoadingSample)"
            @drop="handleVisualizationContainerDrop"
            @visualization-selected="handleVisualizationSelected"
          />
          <div v-if="internalEditAsSequence && isSequence" class="visualization__horizontal-divider" />
          <AnimationControls
            v-if="isMounted && (internalEditAsSequence || sequenceId)"
            ref="animationControls"
            :sequenceId="sequenceId"
            :datasetId="datasetId"
            :reviewSettings="reviewSettings"
            :labels="labels"
            :selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
            @new-frame="sam2SidebarNewFrameHandler"
          />
        </div>

        <BaseSidebar v-if="drawMode === 'sam2'" :collapsible="false">
          <div class="annotation-editor-modal__sidebar">
            <SAM2Sidebar
              ref="sam2Sidebar"
              @mode="(params) => drawMode=params"
              @new-frame="handleUpdateCacheForNewFrame"
              @server-inactive="handleAigtServerInactive"
            />
          </div>
        </BaseSidebar>

        <BaseSidebar
          :title="'Annotation Info'"
          :closeable="true"
          :collapsible="false"
          :placement="'right'"
          :show="showAnnotationInfo"
          @closed="showAnnotationInfo = false"
        >
          <AnnotationDetails :annotationObj="selectedAnnotationInfo" />
        </BaseSidebar>
        <BaseSidebar
          v-if="isAuditMode && reviewSettings && submitType === 'submit-accepted-annotations'"
          class="annotation-review-sidebar"
          :title="'Annotation Review'"
          :closeable="false"
          :collapsible="false"
          :placement="'right'"
          :show="true"
        >
          <div class="annotation-review-sidebar__buttons">
            <button
              class="button button-sm button-secondary"
              :title="'Accept All Annotations'"
              @click="acceptAllAnnotations"
            >
              Accept All Annotations
            </button>
          </div>
          <div class="annotation-review-sidebar__annotations scrollbar">
            <div
              v-for="(annotationObj, index) in currentSample.annotations"
              :key="index"
              class="annotation-review-sidebar__snap-container"
            >
              <GalleryGridAnnotationReviewItem
                v-model:reviewStatus="annotationObj.reviewStatus"
                :imageObj="currentSample.imageObj"
                :annotationSets="annotationSets"
                :labels="labels"
                :annotation="annotationObj"
                @edit-clicked="handleAnnotationReviewItemEdit"
              />
            </div>
          </div>
        </BaseSidebar>
      </div>
      <AnnotationEditorHistoryDrawer
        v-if="hasReviewTask && reviewSettings !== null && reviewSettings !== undefined"
        :active="true"
        :images="doneImages"
        :currentImage="currentTaskImage"
        :pendingImages="pendingTaskImages"
        :activeIndex="activeImageIndex"
        :component="'footer'"
        :reviewSettings="reviewSettings"
        :sequences="sequences"
        @image-clicked="handleTaskImageClicked"
        @update:selected-sequence="handleSelectedSequenceChanged"
      />
    </v-layout>
  </template>
  <template v-else-if="auditorType === 'annotation'">
    <AnnotationReview
      v-if="hasSourceAnnotationSet && reviewSettings"
      ref="annotationReview"
      :reviewSettings="reviewSettings"
      :activeImageIndex="activeImageIndex"
      :currentTaskImage="currentTaskImage"
      :pendingImages="pendingTaskImages"
      :annotations="currentSample.annotations"
      :labels="labels"
      @image-clicked="handleTaskImageClicked"
    />
  </template>

  <template v-if="showLayouts">
    <VisualizationPane
      v-if="visualizationLocations?.canvas"
      :paneType="'canvas'"
      :to="getLocationId(visualizationLocations.canvas)"
      @pane-clicked="handlePaneClick"
    >
      <AnnotationToolViewingModeControls
        :hasImage="currentSample.imageObj && 'id' in currentSample.imageObj"
        :canvasScale="canvasScale"
        :hasSelection="selectedAnnotationIdentifiers.length > 0"
        @zoom-to-fit="handleZoomToFit"
        @zoom-to-selection="handleZoomToSelection"
        @download-image="downloadImage"
      />
      <DrawingToolModeControls
        v-if="mode === 'edit'"
        ref="drawingToolModeControls"
        v-model:mode="drawMode"
        v-model:size="drawCursorSize"
        v-model:keyFrameEnabled="keyFrameEnabled"
        :shouldEnableEraser="selectedAnnotationIdentifiers.length === 1"
        :imageDimensions="imageDimensions"
        :isSequence="isSequence"
        :hasLabels="labels && labels.length > 0"
      />
      <div
        ref="annotationTool"
        class="annotation-tool"
      >
        <template v-if="mode === 'edit'">
          <AnnotationCanvas
            v-if="currentSample.imageObj"
            ref="annotationCanvas"
            v-model:updatedAnnotations="currentSample.annotations"
            v-model:scale="canvasScale"
            v-model:selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
            :selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
            :selectedLabels="annotationDisplaySettings.displayLabels.labels"
            :displayLabelTags="displayLabelTags"
            :annotations="currentSample.annotations"
            :annotationSets="annotationSets"
            :imageObj="currentSample.imageObj"
            :labels="labels"
            :selectedLabel="selectedLabel"
            :colorBy="colorBy"
            :showAnnotations="showAnnotations"
            :editingEnabled="true"
            :useImageCacheOnly="(internalEditAsSequence || isSequence) ? true : false"
            :imageCacheElem="currentSample?.image"
            :highlightedAnnotationIdentifier="hoveredAnnotationListIdentifier"
            :hiddenAnnotationIdentifiers="hiddenAnnotationIdentifiers"
            :isAuditMode="isAuditMode"
            :displayProjection="displayProjection"
            :showTypes="showTypes"
            :drawMode="drawMode"
            :drawCursorSize="drawCursorSize"
            :doneVertexDraw="doneVertexDraw"
            :lidarProjections="currentSample?.lidarProjections"
            :pcdProjections="currentSample?.pcdProjections"
            :pendingAnnotations="pendingAnnotations"
            @create-annotation="createNewInternalAnnotation"
            @image-loaded="handleImageLoaded"
            @remove-annotation="handleAnnotationDeleteClicked"
          />
        </template>
        <template v-else-if="mode === 'view'">
          <AnnotationCanvas
            v-if="currentSample.imageObj"
            ref="annotationCanvas"
            v-model:scale="canvasScale"
            v-model:selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
            :selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
            :selectedLabels="annotationDisplaySettings.displayLabels.labels"
            :annotations="currentSample.annotations"
            :annotationSets="annotationSets"
            :imageObj="currentSample.imageObj"
            :labels="labels"
            :colorBy="colorBy"
            :showAnnotations="showAnnotations"
            :hiddenAnnotationIdentifiers="hiddenAnnotationIdentifiers"
            :displayLabelTags="displayLabelTags"
            :editingEnabled="false"
            :useImageCacheOnly="true"
            :imageCacheElem="currentSample?.image"
            :displayProjection="displayProjection"
            :showTypes="showTypes"
            :showPointerAxes="false"
            :lidarProjections="currentSample?.lidarProjections"
            :pcdProjections="currentSample?.pcdProjections"
          />
        </template>
        <div v-else-if="isMounted && !internalEditAsSequence" id="no-image-remaining">No Images Remaining</div>
      </div>
      <VideoControls v-if="isVideo" />
    </VisualizationPane>
    <VisualizationPane
      v-if="visualizationLocations?.['3d-canvas']"
      :paneType="'3d-canvas'"
      :to="getLocationId(visualizationLocations['3d-canvas'])"
      @pane-clicked="handlePaneClick"
    >
      <PointCloudViewer
        v-model:updatedAnnotations="currentSample.annotations"
        v-model:selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
        :imageObj="currentSample.imageObj"
        :annotations="currentSample.annotations"
        :pendingAnnotations="sam2Annotations"
        :labels="labels"
        :selectedLabel="selectedLabel"
        :selectedLabels="annotationDisplaySettings.displayLabels.labels"
        :selectedAnnotationSets="annotationDisplaySettings.displayAnnotationSets.annotation_sets"
        :hiddenAnnotationIdentifiers="hiddenAnnotationIdentifiers"
        :isAuditMode="isAuditMode"
        :currentTool="current3DCanvasTool"
        :showTypes="showTypes"
        :points="currentSample?.pcd"
        :lidar="currentSample?.lidar"
        :editingEnabled="mode === 'edit'"
        @create-annotation="createNewInternalAnnotation"
      />
      <DrawingToolModeControls3d
        v-if="mode === 'edit'"
        v-model:current3DCanvasTool="current3DCanvasTool"
        :hasLabels="labels && labels.length > 0"
      />
    </VisualizationPane>

    <VisualizationPane
      v-if="visualizationLocations?.['depth-map']"
      :paneType="'depth-map'"
      :to="getLocationId(visualizationLocations['depth-map'])"
      @pane-clicked="handlePaneClick"
    >
      <DepthMapViewer
        :showDepthMap="showTypes?.depthmap"
      />
    </VisualizationPane>

    <VisualizationPane
      v-if="visualizationLocations?.['location-map']"
      :paneType="'location-map'"
      :to="getLocationId(visualizationLocations['location-map'])"
      @pane-clicked="handlePaneClick"
    >
      <LocationMapViewer
        :imageObj="currentSample.imageObj"
        :annotations="currentSample.annotations"
        :showLocation="showTypes?.gps"
      />
    </VisualizationPane>

  </template>
  <BulkDeleteAnnotationModal
    ref="bulkDeleteModal"
    :frameCount="frameCount"
    :imageObj="currentSample?.imageObj"
    :annotationSets="annotationSets"
    @deleted="handleAnnotationBulkDeleteComplete"
  />
  <CreateAnnotationSetModal
    ref="createAnnotationSetModal"
    :datasetId="sourceDatasetId"
    @annotation-set-created="newAnnotationSetCreated"
  />
  <CreateLabelsModal
    ref="createLabelsModal"
    :datasetId="sourceDatasetId"
    @label-created="newLabelsCreated"
  />
</template>




<script setup>
import BaseSidebar from '@/components/BaseSidebar.vue';
import AnnotationCanvas from '@/components/DatasetComponent/AnnotationTool/AnnotationCanvas.vue';
import AnnotationToolRibbon from '@/components/DatasetComponent/AnnotationTool/AnnotationToolRibbon.vue';
import AnnotationToolSequenceRibbon from '@/components/DatasetComponent/AnnotationTool/AnnotationToolSequenceRibbon.vue';
import AnnotationToolAGTGRibbon from '@/components/DatasetComponent/AnnotationTool/AnnotationToolAGTGRibbon.vue';
import AnnotationDetails from '@/components/DatasetComponent/AnnotationTool/AnnotationDetails.vue';
import GalleryGridAnnotationReviewItem from '@/components/DatasetComponent/GalleryComponent/GalleryGridAnnotationReviewItem.vue';
import AnnotationEditorHistoryDrawer from '@/components/DatasetComponent/AnnotationTool/AnnotationEditorHistoryDrawer.vue';
import AnnotationReview from '@/components/DatasetComponent/AnnotationTool/AnnotationReview.vue';
import ImageGroupSelector from '@/components/ImageGroupSelector.vue';
import {
  ref, toRefs, watch, computed, onMounted, onUnmounted, nextTick,
} from 'vue';
import useTasks from '@/composables/annotationTool/useTasks.js';
import { useEditorTaskStore } from '@/stores/useEditorTaskStore.js';
import useAnnotationEditorModalHotkeys from '@/composables/annotationTool/useAnnotationEditorModalHotkeys.js';
import VideoControls from '@/components/DatasetComponent/AnnotationTool/VideoControls/VideoControls.vue';
import { useViewerVisualizationsStore } from '@/stores/useViewerVisualizationsStore.js';
import { useEditorControlsStore } from '@/stores/useEditorControlsStore.js';
import { useEditorStore } from '@/stores/useEditorStore.js';
import { storeToRefs } from 'pinia';
import PointCloudViewer from '@/components/DatasetComponent/AnnotationTool/PointCloudViewer.vue';
import JsonRawViewer from '@/components/DatasetComponent/AnnotationTool/JsonRawViewer.vue';
import { useStore } from 'vuex';
import AnnotationToolViewingModeControls from '@/components/DatasetComponent/AnnotationTool/AnnotationToolViewingModeControls.vue';
import BulkDeleteAnnotationModal from '@/components/DatasetComponent/AnnotationTool/BulkDeleteAnnotationModal.vue';
import DepthMapViewer from '@/components/DatasetComponent/AnnotationTool/DepthMapViewer.vue';
import LocationMapViewer from '@/components/DatasetComponent/AnnotationTool/LocationMapViewer.vue';
import DrawingToolModeControls from '@/components/DatasetComponent/AnnotationTool/DrawingToolModeControls.vue';
import DrawingToolModeControls3d from '@/components/DatasetComponent/AnnotationTool/DrawingToolModeControls3d.vue';
import VisualizationPane from '@/components/DatasetComponent/AnnotationTool/VisualizationPane.vue';
import VisualizationDisplays from '@/components/DatasetComponent/AnnotationTool/VisualizationDisplays.vue';
import SelectLayoutMenu from '@/components/DatasetComponent/AnnotationTool/SelectLayoutMenu.vue';
import AddVisualizationMenu from '@/components/DatasetComponent/AnnotationTool/AddVisualizationMenu.vue';
import useVisualizationDisplays from '@/composables/annotationTool/useVisualizationDisplays.js';
import AnimationControls from '@/components/DatasetComponent/AnnotationTool/AnimationControlsV2.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useSAM2Store } from '@/stores/useSAM2Store.js';
import SAM2Sidebar from '@/components/DatasetComponent/AnnotationTool/SAM2Sidebar.vue';
import useAnnotationType from '@/composables/useAnnotationType.js';
import { useRoute, useRouter } from 'vue-router';
import useAnnotationColorMap from '@/composables/useAnnotationColorMap.js';
import useGallery from '@/composables/annotationTool/useGallery.js';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import CreateLabelsModal from '@/components/DatasetComponent/DatasetManagement/CreateLabelsModal.vue';
import CreateAnnotationSetModal from '@/components/DatasetComponent/DatasetManagement/CreateAnnotationSetModal.vue';
import { getAnnotationTypes, annotationTypeHiddenKey } from '@/assets/js/utils.js';
import useBrushToPolygon from '@/composables/annotationTool/useBrushToPolygon.js';

const props = defineProps({
  projectId: {
    type: [String, Number],
    default: null
  },
  initialAnnotationDisplaySettings: {
    type: Object,
    default: () => (
      {
        displayAnnotationSets: { annotation_sets: [] },
        displayLabels: { labels: [] },
        colorAnnotationsBy: { type: "label" },
      }
    ),
  },
});

const {
  initialAnnotationDisplaySettings,
} = toRefs(props);

const store = useStore();
const route = useRoute();
const router = useRouter();
const $toast = useToast();

const canEdit = computed(() => {
  if (!store.state.user.user) {
    return false;
  }
  // TODO: add canEdit based on user permissions
  return true;
});

const visualizationStore = useViewerVisualizationsStore();
const {
  removeKeyframe, setKeyframeNew, enableKeyFrames
} = visualizationStore;

const {
  datasetFilters,
  setDatasetFilters,
  annotationDisplaySettings,
} = useGallery();
if (!annotationDisplaySettings.value) {
  annotationDisplaySettings.value = {
    displayAnnotationSets: { annotation_sets: initialAnnotationDisplaySettings.value.displayAnnotationSets.annotation_sets },
    displayLabels: { labels: initialAnnotationDisplaySettings.value.displayLabels.labels },
    colorAnnotationsBy: { type: initialAnnotationDisplaySettings.value.colorAnnotationsBy.type },
  };
}

const editorStore = useEditorStore();
const {
  mode,
  currentSample,
  prevInternalAnnotations,
  sequences,
  imageGroupId,
  datasetId,
  sequenceId,
  isSequence,
  isVideo,
  labels,
  annotationSets,
  annotationSetToEdit,
  imageDimensions,
  canvasScale,
  selectedAnnotationIdentifiers,
  annotationIdentifierMap,
  selectedAnnotations,
  selectedAnnotationInfo,
  hasCopiedAnnotations,
} = storeToRefs(editorStore);
const {
  $reset: resetEditorStore,
  handleImageLoaded,
  selectAnnotation,
  handleUndo,
  handleRevertChanges,
  handleCopyAnnotations,
  handlePasteAnnotations,
  copyAnnotationsObject,
  setCurrentAnnotations,
  addToCurrentAnnotations,
} = editorStore;
onUnmounted(() => {
  resetEditorStore();
  editorStore.$dispose();
});
const labelColorMap = ref({});

const {
  jsonViewerShowTypes: showTypes,
} = useAnnotationType();

const annotationCanvas = ref(null);
// const annotationSetToEdit = ref(null);
const bulkDeleteModal = ref(null);
const createAnnotationSetModal = ref(null);
const createLabelsModal = ref(null);
const annotationToolSequenceRibbon = ref(null);
const annotationToolAGTGRibbon = ref(null);
const drawingToolModeControls = ref(null);
const sam2Sidebar = ref(null);
const keyFrameEnabled=ref(false);

const displayLabelTags = ref({
  box: true,
});
const displayProjection = ref({
  '3dbox': false,
  lidar: false,
  pcd: false,
});
const showAnnotations = ref(true);
const showAnnotationInfo = ref(false);
const hoveredAnnotationListIdentifier = ref(null);
const hiddenAnnotationIdentifiers = ref([]);
const activePane = ref(null);
const isMounted = ref(false);
const internalEditAsSequence = ref(false);

// SAM2
const sam2Store = useSAM2Store();
const { $reset: resetSAM2 } = sam2Store;
const {
  isPropagating,
  sam2Server,
  isSam2ServerAvailable,
  sam2Annotations,
  aigt3dAnnotations,
  aigt2dAnnotations,
} = storeToRefs(sam2Store);
onMounted(() => {
  resetSAM2();
});
onUnmounted(() => {
  resetSAM2();
});

function sam2SidebarNewFrameHandler() {
  if (sam2Server.value && isSam2ServerAvailable.value) {
    sam2Sidebar.value.handleNewFrame();
  }
}

function handleAigtServerInactive() {
  drawMode.value = 'pointer';
  drawingToolModeControls.value?.confirmLaunchAGTGServer();
}

async function handleUpdateCacheForNewFrame() {
  await animationControls.value.cacheImageForNewFrame();
}

// Setup Tasks
const {
  reviewSettings,
  currentTaskImage,
  pendingTaskImages,
  sourceDatasetId,
  sourceAnnotationSet,
  hasSourceAnnotationSet,
  destinationAnnotationSet,
  isAuditMode,
  groups,
  auditorType,
  hasReviewTask,
  setupTaskImages,
  isFetchingNewImage,
  handleLabellingSubmitAnnotations,
  acceptAllAnnotations,
  activeImageIndex,
  doneImages,
  switchToTaskImage,
  submitType,
  handleAuditSubmitAllAnnotations,
  handleAuditSubmitAcceptedAnnotations,
  handleFrameAuditSubmitAllAnnotations,
  handleFrameAuditSubmitAcceptedAnnotations,
  skipTaskImage,
  getTaskSequences,
  doneImageOpen,
} = useTasks();
const editorTaskStore = useEditorTaskStore();
const {
  $reset: resetTaskStore,
} = editorTaskStore;
onUnmounted(() => {
  resetTaskStore();
  editorTaskStore.$dispose();
});
if (reviewSettings.value?.initialAuditorType) {
  auditorType.value = reviewSettings.value?.initialAuditorType;
} else if (route.query.auditorType) {
  auditorType.value = route.query.auditorType;
}
watch(auditorType, async (old, newval) => {
  isFetchingNewImage.value = true;

  await setupTaskImages();

  isFetchingNewImage.value = false;
});

// Controls
const editorControlsStore = useEditorControlsStore();
const {
  selectedLabel, drawMode, current3DCanvasTool, drawCursorSize, doneVertexDraw,
} = storeToRefs(editorControlsStore);
onUnmounted(() => {
  const { $reset } = useEditorControlsStore();
  $reset();
});

// Create hotkeys
const viewHotkeys = {
  'z': () => {
    handleZKey();
  },
};
const canvasHotkeys = {
  'Escape': () => {
    if (activePane.value === 'canvas') {
      selectedAnnotationIdentifiers.value = [];
      drawMode.value = 'pointer';
    } else if (activePane.value === '3d-canvas') {
      selectedAnnotationIdentifiers.value = [];
      selectedLabel.value = labels.value[0];
      current3DCanvasTool.value = '3d-pointer';
    }
  },
};
const canvas3DHotkeys = {
  'q': () => { current3DCanvasTool.value = '3d-pointer'; },
  'b': () => { current3DCanvasTool.value = '3d-box'; },
  'c': () => { current3DCanvasTool.value = '3d-cluster'; },
  'l': () => { current3DCanvasTool.value = '3d-edit-label'; },
  'g': () => { current3DCanvasTool.value = 'translate-object'; },
  's': () => { current3DCanvasTool.value = 'scale-object'; },
  'Escape': async () => {
    selectedAnnotationIdentifiers.value = [];
    selectedLabel.value = labels.value[0];
    current3DCanvasTool.value = '3d-pointer';
  },
};
const toolHotkeys = ref({
  ...canvasHotkeys,
});
const auditHotkeys = ref({
  'v': () => {
    selectedAnnotations.value.forEach((anno) => {
      anno.reviewStatus = 'verified';
    });
  },
  'V': () => {
    selectedAnnotations.value.forEach((anno) => {
      anno.reviewStatus = 'unreviewed';
    });
  },
  ' ': () => {
    selectedAnnotations.value.forEach((anno) => {
      if (anno.reviewStatus === 'verified') {
        anno.reviewStatus = 'unreviewed';
      } else {
        anno.reviewStatus = 'verified';
      }
    });
  },
  'Enter': async () => {
    if (submitType.value === 'submit-all-annotations') {
      if (internalEditAsSequence.value || isSequence.value) {
        handleFrameAuditSubmitAllAnnotations(currentSample.value.imageObj);
        currentSample.value.imageObj.annotations = currentSample.value.annotations;
        handleSequenceSubmit();
      } else {
        handleAuditSubmitAllAnnotations();
      }
    } else if (submitType.value === 'submit-accepted-annotations') {
      if (internalEditAsSequence.value || isSequence.value) {
        handleFrameAuditSubmitAcceptedAnnotations(currentSample.value.imageObj);
        currentSample.value.imageObj.annotations = currentSample.value.annotations;
        handleSequenceSubmit();
      } else {
        handleAuditSubmitAcceptedAnnotations();
      }
    }
  },
});
const drawToolHotKeys = ref({
  'q': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = 'pointer';
    } else if (activePane.value === '3d-canvas') {
      current3DCanvasTool.value = '3d-pointer';
    }
  },
  'b': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = "box";
    } else if (activePane.value === '3d-canvas') {
      current3DCanvasTool.value = '3d-box';
    }
  },
  'c': () => {
    if (activePane.value === '3d-canvas') {
      current3DCanvasTool.value = '3d-cluster';
    }
  },
  'a': () => {
    if (activePane.value === 'canvas') {
      drawingToolModeControls.value.confirmLaunchAGTGServer();
    }
  },
  'w': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = "brush";
    }
  },
  'l': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = 'color';
    } else if (activePane.value === '3d-canvas') {
      current3DCanvasTool.value = '3d-edit-label';
    }
  },
  'p': () => {
    if (activePane.value === 'canvas') {
      drawMode.value = 'line';
    }
  },
  'e': () => {
    if (activePane.value === 'canvas') {
      if (selectedAnnotationIdentifiers.value.length === 1) {
        drawMode.value = "eraser";
      }
    }
  },
   'k': () => {
    if (activePane.value === 'canvas') {
        keyFrameEnabled.value = !keyFrameEnabled;
    }
  },
  '=': () => {
    if (activePane.value === 'canvas') {
      if (drawCursorSize.value < (0.2 * imageDimensions.value.width)) {
        drawCursorSize.value *= 1.3;
      }
    }
  },
  '-': () => {
    if (activePane.value === 'canvas') {
      if (drawCursorSize.value > (0.001 * imageDimensions.value.width)) {
        drawCursorSize.value /= 1.3;
      }
    }
  },
});

watch(drawMode, () => {
  if (drawMode.value !== "line") {
    nextTick(() => { doneVertexDraw.value = true; });
  } else {
    nextTick(() => { doneVertexDraw.value = false; });
  }
});

watch(selectedAnnotationIdentifiers,(v)=>{
  if (!keyFrameEnabled.value) return;

  if(selectedAnnotationIdentifiers.value.length>0) {
    const anID=selectedAnnotationIdentifiers.value[0];
    //Get annotation value
    for(let k=0;k<currentSample.value.annotations.length; k++ ) {
      const anno=currentSample.value.annotations[k];
      if (anno.id==anID) {
        setKeyframeNew(frame.value, currentSample.value.imageObj, anno);
      } 
    }
  } 
});

watch(keyFrameEnabled,(v)=>{
  enableKeyFrames(v);

})

const imageAuditHotkeys = ref({
  'ArrowRight': async () => {
    if (!isPropagating.value) {
      await skipTaskImage();
    }
  },
});
const sequenceControlHotkeys = ref({
  'ArrowLeft': async () => {
    if (!isPropagating.value) {
      await animationControls.value.handleSkipPrevious();
    }
  },
  'ArrowRight': async () => {
    if (!isPropagating.value) {
      await animationControls.value.handleSkipNext();
    }
  },
});
const taskImageAuditHotkeys = computed(() => ({
  'Delete': async () => {
    if (selectedAnnotationIdentifiers.value.length > 0) {
      const annotationsToDelete = selectedAnnotationIdentifiers.value.map((identifier) => annotationIdentifierMap.value[identifier]);
      setCurrentAnnotations([...currentSample.value.annotations.filter((anno) => !annotationsToDelete.includes(anno))]);
      selectedAnnotationIdentifiers.value = [];
    }
  },
  'ctrl,c': () => {
    handleCopyAnnotations();
  },
  'ctrl,v': () => {
    handlePasteAnnotations();
  },
  'ctrl,x': () => {
    handleDeleteAnnotationBulk();
  },
  ...viewHotkeys,
  ...auditHotkeys.value,
  ...(internalEditAsSequence.value || isSequence.value ? sequenceControlHotkeys.value : imageAuditHotkeys.value),
  ...toolHotkeys.value,
  ...drawToolHotKeys.value,
}));

const imageEditorHotkeys = computed(() => ({
  'Delete': async () => {
    if (selectedAnnotationIdentifiers.value.length > 0) {
      const annotationsToDelete = selectedAnnotationIdentifiers.value.map((identifier) => annotationIdentifierMap.value[identifier]);
      setCurrentAnnotations([...currentSample.value.annotations.filter((anno) => !annotationsToDelete.includes(anno))]);
      selectedAnnotationIdentifiers.value = [];
    }
  },
  'ctrl,c': () => {
    handleCopyAnnotations();
  },
  'ctrl,v': () => {
    handlePasteAnnotations();
  },
  'ctrl,x': () => {
    handleDeleteAnnotationBulk();
  },
  'Enter': async () => {
    if (annotationToolSequenceRibbon.value) {
      await annotationToolSequenceRibbon.value.handleLabellingSubmit();
    } else if (annotationToolAGTGRibbon.value) {
      await annotationToolAGTGRibbon.value.handleSaveSAM2Annotations();
    }
  },
  ...viewHotkeys,
  ...(internalEditAsSequence.value || isSequence.value ? sequenceControlHotkeys.value : {}),
  ...toolHotkeys.value,
  ...drawToolHotKeys.value,
}));

const imageViewerHotkeys = computed(() => ({
  ...viewHotkeys,
  ...(internalEditAsSequence.value || isSequence.value ? sequenceControlHotkeys.value : {}),
  ...toolHotkeys.value,
}));

const {
  areListenersActive: areAuditListenersActive,
  activateListeners: activateImageAuditListeners,
  deactivateListeners: deactivateImageAuditListeners,
} = useAnnotationEditorModalHotkeys(taskImageAuditHotkeys);
const {
  areListenersActive: areEditorListenersActive,
  activateListeners: activateImageEditorListeners,
  deactivateListeners: deactivateImageEditorListeners,
} = useAnnotationEditorModalHotkeys(imageEditorHotkeys);
const {
  areListenersActive: areViewerListenersActive,
  activateListeners: activateImageViewerListeners,
  deactivateListeners: deactivateImageViewerListeners,
} = useAnnotationEditorModalHotkeys(imageViewerHotkeys);
watch(auditorType, (auditorTypeVal) => {
  if (auditorTypeVal !== 'image') {
    deactivateImageAuditListeners();
  } else {
    if (!areAuditListenersActive.value) {
      activateImageAuditListeners();
    }
  }

  // Toggle showing the visualizations
  if (auditorTypeVal === 'annotation') {
    showLayouts.value = false;
  } else {
    nextTick(() => {
      showLayouts.value = true;
    });
  }
});
watch(mode, (modeVal) => {
  if (modeVal === 'edit' && auditorType.value === 'image') {
    nextTick(() => { prevInternalAnnotations.value = null; });
    if (areViewerListenersActive.value) {
      deactivateImageViewerListeners();
    }
    activateImageEditorListeners();
  } else {
    handleRevertChanges();
    if (areEditorListenersActive.value) {
      deactivateImageEditorListeners();
    }
    activateImageViewerListeners();
  }
});

if (reviewSettings.value?.initialAuditorType === 'image' || route.query.auditorType === 'image') {
  activateImageAuditListeners();
} else if (mode.value === 'edit') {
  activateImageEditorListeners();
} else if (mode.value === 'view') {
  activateImageViewerListeners();
}

// Zoom
function handleZoomToFit() {
  annotationCanvas.value.zoomToFitImage();
}
function handleZoomToSelection() {
  annotationCanvas.value.zoomToSelection();
}
function handleZKey() {
  if (selectedAnnotationIdentifiers.value.length > 0) {
    annotationCanvas.value.zoomToSelection();
  } else {
    annotationCanvas.value.zoomToFitImage();
  }
}

function filterAnnotationsBySet(annotations, sets) {
  if (sets) {
    // should return all those with the current annotation set and those without
    // the ones without annotation set are recently created
    return annotations.filter((anno) => sets.includes(anno.annotation_set_id) || !anno.annotation_set_id);
  }
  return annotations;
}

// Computed
const colorBy = computed(() => annotationDisplaySettings.value?.colorAnnotationsBy?.type);

// Maps an annotation to every sidebar topic group it belongs to (an annotation can now
// carry box + segmentation + 3D box data simultaneously). Non-shape types (pcd/radar/mask/etc.)
// still fall back to the legacy `type` field since getAnnotationTypes doesn't cover those.
function getTopicKeysForAnnotation(item) {
  const shapeTypes = getAnnotationTypes(item);
  const keys = [];
  if (shapeTypes.includes('box')) keys.push('box');
  if (shapeTypes.includes('segmentation')) keys.push('seg');
  if (shapeTypes.includes('box3d')) keys.push('3dbox');
  if (keys.length === 0) {
    keys.push(item.type);
  }
  return keys;
}

const groupedAnnotations = computed(() => {
  if ((internalEditAsSequence.value || isSequence.value) && currentSample.value) {
    // For animation
    const groupedData = {};
    topics.value.forEach((key) => {
      groupedData[key] = [];
    });

    const totalAnnotations = [...currentSample.value.annotations, ...Object.values(pendingAnnotations.value).flat()];
    const filteredAnnotations = filterAnnotationsBySet(totalAnnotations, annotationDisplaySettings.value.displayAnnotationSets.annotation_sets);
    filteredAnnotations.forEach((item) => {
      getTopicKeysForAnnotation(item).forEach((itemType) => {
        if (!groupedData[itemType]) {
          groupedData[itemType] = [];
        }
        groupedData[itemType].push(item);
      });
    });
    return groupedData;
  } else {
    // For non-animation
    if (!currentSample.value.annotations) return {};
    const groupedData = {};
    const filteredAnnotations = filterAnnotationsBySet(currentSample.value.annotations, annotationDisplaySettings.value.displayAnnotationSets.annotation_sets);
    filteredAnnotations.forEach((item) => {
      getTopicKeysForAnnotation(item).forEach((itemType) => {
        if (!groupedData[itemType]) {
          groupedData[itemType] = [];
        }
        groupedData[itemType].push(item);
      });
    });
    return groupedData;
  }
});

// Watchers

// watch(() => currentSample.value.imageObj, async () => {
//   setCurrentAnnotations(currentSample.value?.imageObj?.annotations);
//   nextTick(() => { prevInternalAnnotations.value = null; });

//   if (isSequence.value) {
//     topics.value = await getTopics();
//   }
// });

watch(() => currentSample.value.annotations, (newAnnotations, prevAnnotations) => {
  prevInternalAnnotations.value = JSON.parse(JSON.stringify(prevAnnotations || []));

  if (reviewSettings.value?.reviewTask?.type === 'audit' && doneImageOpen.value) {
    newAnnotations.forEach((anno) => {
      anno.reviewStatus = 'verified';
    });
  }
}, { deep: true });

watch(annotationSetToEdit, () => {
  if (annotationSetToEdit.value) {
    annotationDisplaySettings.value.displayAnnotationSets.annotation_sets = [annotationSetToEdit.value.id];
  }
});

watch(imageDimensions, () => {
  if (imageDimensions.value) {
    drawCursorSize.value = 0.04 * imageDimensions.value.width;
  }
});

watch(showTypes, () => {
  if (showTypes.value.pcd || showTypes.value['3dbox'] || showTypes.value.lidar || showTypes.value.imu) {
    addVisualizationToLayout('3d-canvas');
  }
  if (showTypes.value.depthmap) {
    addVisualizationToLayout("depth-map");
  }
  if (showTypes.value.gps) {
    addVisualizationToLayout('location-map');
  }
}, { deep: true });

async function handleRouteParams() {
  if (route.query.annotationDisplaySettings) {
    annotationDisplaySettings.value = JSON.parse(route.query.annotationDisplaySettings);

    if (!annotationDisplaySettings.value) {
      annotationDisplaySettings.value = {
        displayAnnotationSets: { annotation_sets: [] },
        displayLabels: { labels: [] },
        colorAnnotationsBy: { type: "label" },
      };
    }

  }
  if (route.query.showTypes) {
    showTypes.value = JSON.parse(route.query.showTypes);
  }
  // if (this.$route.query.imagesParams) {
  //   this.setImageParams(JSON.parse(this.$route.query.imagesParams));
  // }
  if (route.query.datasetFilters) {
    setDatasetFilters(JSON.parse(route.query.datasetFilters));
  }

  if (route.query.reviewTaskId) {
    reviewSettings.value=null;

    const reviewTask = await getReviewTask(parseInt(route.query.reviewTaskId));
  
    const reviewSession = await createReviewSession(parseInt(route.query.reviewTaskId));

    reviewSettings.value = {
      reviewSessionMethod: 'review_annotation_editor',
      batchSize: 10,
      samplingMethod: 'random',
      reviewSession: reviewSession,
      reviewTask,
      initialAuditorType: auditorType.value,
    };

  
  }
}

async function getDatasetInfo(id) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getDatasetById({
    dataset_id: id,
  })
    .catch((error) => {
      $toast.open({
        queue: true,
        message: `Failed to retrieve dataset: ${error}`,
        type: 'error',
        position: 'top',
        dismissible: true,
      });
    });
  if (!resp || resp.error || !resp.result) {
    $toast.open({
      queue: true,
      message: `Failed to retrieve dataset: ${resp.error.message}`,
      type: 'error',
      position: 'top',
      dismissible: true,
    });
    return null;
  }
  return resp.result;
}

async function createAnnotationSet(name, dataset_id) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.createAnnotationSet({
    name,
    description: "",
    operator: store.state.user.user.username,
    origin_x: 0,
    origin_y: 0,
    dataset_id,
  })
    .catch((error) => {
      $toast.open({
        queue: true,
        message: `Failed to create annotation set: ${error}`,
        type: 'error',
        position: 'top',
        dismissible: true,
      });
    });
  if (resp.error || !resp.result) {
    $toast.open({
      queue: true,
      message: `Failed to create annotation set: ${resp.error.message}`,
      type: 'error',
      position: 'top',
      dismissible: true,
    });
  }
}

onMounted(async () => {
  if (!canEdit.value) {
    mode.value = 'view';
  }

  if (route.query.image_id === '') {
    const query = { ...route.query };
    delete query.image_id;
    await router.replace({ path: route.path, query, hash: route.hash });
  }

  await handleRouteParams();

  if (reviewSettings.value?.reviewTask?.id) {
    // Setup for review task
    if (hasSourceAnnotationSet.value) {
      annotationSetToEdit.value =   sourceAnnotationSet.value;
    } else {
      annotationSetToEdit.value =   destinationAnnotationSet.value;
    }

    // Get all sequences for the dataset
    if (hasReviewTask.value) {
      sequences.value = await getTaskSequences();
    }

    // Setup image and annotations
    refreshAnnotationSets(reviewSettings.value.reviewTask.dataset_id)
      .then(() => {
        if (annotationSets.value.length === 0) {
          $toast.open({
            queue: true,
            message: "An annotation set is required to start editing. Create annotation sets from sidebar to edit.",
            type: 'error',
            position: 'top',
            dismissible: true,
          });
        }
      });
    refreshLabels(reviewSettings.value.reviewTask.dataset_id)
      .then(() => {
        annotationDisplaySettings.value.displayLabels.labels = [...labels.value.map((l) => l.index)];
        if (labels.value.length > 0) {
          selectedLabel.value = labels.value[0];
        }
        if (labels.value.length === 0) {
          $toast.open({
            queue: true,
            message: "Labels are required for editing annotations. Create labels from sidebar to start.",
            type: 'error',
            position: 'top',
            dismissible: true,
          });
        }
      });
    await setupTaskImages();
  } else {
    // Setup for gallery viewer/editor
    internalEditAsSequence.value = true;
    selectedLabel.value = labels.value[0];

    const datastore = new DatastoreConnect();
    let imageId = Number.parseInt(route.query.image_id, 10);
    if (!Number.isFinite(imageId)) {
      const sequenceIdFromRoute = Number.parseInt(route.query.sequence_id, 10);
      const datasetIdFromRoute = route.params.datasetId
        ? Number.parseInt(route.params.datasetId, 16)
        : Number.parseInt(route.query.dataset, 10);

      if (!Number.isFinite(sequenceIdFromRoute) || !Number.isFinite(datasetIdFromRoute)) {
        $toast.open({
          queue: true,
          message: 'A sequence or image is required to open the viewer.',
          type: 'error',
          position: 'top',
          dismissible: true,
        });
        return;
      }

      const framesResponse = await datastore.getFilteredImages({
        get_annotations: false,
        get_sensors: false,
        reverse: false,
        offset: 0,
        limit: 0,
        only_ids: true,
        combine_sequence_frames: true,
        images_filter: {
          dataset_id: datasetIdFromRoute,
          sequence_id: sequenceIdFromRoute,
        },
        image_files_filter: {},
      });

      if (framesResponse.error) {
        throw new Error(framesResponse.error.message || 'Failed to retrieve sequence frames.');
      }

      const frameIds = Array.isArray(framesResponse.result) ? framesResponse.result : [];
      imageId = Number.parseInt(frameIds[0], 10);
      if (!Number.isFinite(imageId)) {
        $toast.open({
          queue: true,
          message: 'No frames were found for this sequence.',
          type: 'error',
          position: 'top',
          dismissible: true,
        });
        return;
      }
    }

    const params = {
      id: imageId,
      get_annotations: true,
    };
    const imageResult = await datastore.getImage(params)
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch((error) => {
        $toast.open({
          queue: true,
          message: `Failed to get image: ${error.message}`,
          type: 'error',
          position: 'top',
          dismissible: true,
        });
      });

    currentSample.value = {
      image: null,
      imageObj: imageResult,
      annotations: copyAnnotationsObject(imageResult.annotations),
      loaded: false,
    };

    await Promise.all([
      refreshAnnotationSets(imageResult.dataset_id)
        .then(() => {
          if (annotationSets.value.length === 0) {
            createAnnotationSet("annotations", imageResult.dataset_id)
              .then(() => {
                refreshAnnotationSets(imageResult.dataset_id);
              });
          }
          if (annotationSets.value.length > 0 && annotationDisplaySettings.value.displayAnnotationSets.annotation_sets.length === 0) {
            annotationDisplaySettings.value.displayAnnotationSets.annotation_sets = [annotationSets.value[0].id];
          }
        }),
      refreshLabels(imageResult.dataset_id)
        .then(() => {
          if (labels.value.length > 0) {
            selectedLabel.value = labels.value[0];
          }
          if (labels.value.length === 0) {
            $toast.open({
              queue: true,
              message: "Labels are required for editing annotations. Create labels from sidebar to start.",
              type: 'info',
              position: 'top',
            });
          }
        }),
    ]);

    if (canEdit.value && annotationSets.value.length > 0) {
      const annotationSetId = Number.parseInt(route.query.edit_annotation_set_id || route.query.annotation_set_id, 10);
      const annotationSet = annotationSets.value.find((set) => set.id === annotationSetId) || annotationSets.value[0];
      handleStartEditingAnnotations(annotationSet);
    }
  }

  isMounted.value = true;
});

// Methods
const { unionPolygons } = useBrushToPolygon();

// Returns true when the new annotation's geometry was merged into the single selected object.
function mergeIntoSelectedObject(newAnnotation) {
  if (selectedAnnotationIdentifiers.value.length !== 1) {
    return false;
  }
  const target = currentSample.value.annotations.find((anno) => anno.id === selectedAnnotationIdentifiers.value[0]);
  if (!target || (newAnnotation.image_id && target.image_id && target.image_id !== newAnnotation.image_id)) {
    return false;
  }

  const newTypes = getAnnotationTypes(newAnnotation);
  if (newTypes.length === 0) {
    return false;
  }

  const mergedTarget = JSON.parse(JSON.stringify(target));
  if (newTypes.includes('box')) {
    mergedTarget.x = newAnnotation.x;
    mergedTarget.y = newAnnotation.y;
    mergedTarget.w = newAnnotation.w;
    mergedTarget.h = newAnnotation.h;
  }
  if (newTypes.includes('box3d')) {
    mergedTarget.data_json = newAnnotation.data_json;
  }
  if (newTypes.includes('segmentation')) {
    mergedTarget.polygon = newAnnotation.polygon;
  }
  mergedTarget.save_pending = true;
  mergedTarget.status = mergedTarget.status || 'updated';

  setCurrentAnnotations(currentSample.value.annotations.map((anno) => (anno.id === mergedTarget.id ? mergedTarget : anno)));
  return true;
}

async function createNewInternalAnnotation(annotation) {
  if (isAuditMode.value) {
    annotation.reviewStatus = 'verified';
  }
  annotation.annotation_set_id = destinationAnnotationSet.value.id;
  if (getAnnotationTypes(annotation).includes('box3d') && !showTypes.value?.['3dbox']) {
    showTypes.value = { ...showTypes.value, '3dbox': true };
  }
  if (mergeIntoSelectedObject(annotation)) {
    return;
  }
  addToCurrentAnnotations([annotation]);
  selectedAnnotationIdentifiers.value = [annotation.id];
}

function handleTaskImageClicked(imageIndex) {
  switchToTaskImage(imageIndex);
}

function handleAnnotationBulkDeleteComplete() {
  animationControls.value.handleClearCache();
}

function handleDeleteAnnotationBulk() {
  bulkDeleteModal.value.showModal();
}

function validateJoinSelection(annotations) {
  const box2dCount = annotations.filter((anno) => getAnnotationTypes(anno).includes('box')).length;
  const box3dCount = annotations.filter((anno) => getAnnotationTypes(anno).includes('box3d')).length;
  if (box2dCount > 1 || box3dCount > 1) {
    $toast.open({
      queue: true,
      message: 'Cannot join multiple 2D / 3D boxes',
      type: 'error',
      position: 'top',
      dismissible: true,
    });
    return false;
  }
  return true;
}

// Merges the 2D box / 3D box / polygon data from the other selected annotations onto the target, then deletes the sources.
async function joinAnnotationsInto(targetIdentifier) {
  const selected = selectedAnnotations.value;
  if (selected.length < 2 || !validateJoinSelection(selected)) {
    return;
  }

  const targetAnnotation = annotationIdentifierMap.value[targetIdentifier];
  if (!targetAnnotation) {
    return;
  }

  const mergedTarget = JSON.parse(JSON.stringify(targetAnnotation));
  const sourceAnnotations = selected.filter((anno) => anno.id !== targetIdentifier);

  sourceAnnotations.forEach((anno) => {
    const types = getAnnotationTypes(anno);
    if (types.includes('box')) {
      mergedTarget.x = anno.x;
      mergedTarget.y = anno.y;
      mergedTarget.w = anno.w;
      mergedTarget.h = anno.h;
    }
    if (types.includes('box3d')) {
      mergedTarget.data_json = anno.data_json;
    }
    if (types.includes('segmentation')) {
      mergedTarget.polygon = getAnnotationTypes(mergedTarget).includes('segmentation')
        ? unionPolygons(mergedTarget.polygon, anno.polygon)
        : anno.polygon;
    }
  });

  // Don't call annotation.del directly here — removing the source annotations from
  // currentSample.annotations is enough. getAnnotationsForDatabase() diffs the before/after
  // annotation lists and includes anything missing in delete_annotations, which flows into
  // manualannotationtask.audit.submit (and the other submit RPCs) the next time the user submits.
  const removedIdentifiers = sourceAnnotations.map((anno) => anno.id);
  const newAnnotations = currentSample.value.annotations
    .filter((anno) => !removedIdentifiers.includes(anno.id))
    .map((anno) => (anno.id === targetIdentifier ? mergedTarget : anno));

  setCurrentAnnotations(newAnnotations);
  selectedAnnotationIdentifiers.value = [targetIdentifier];
}

function toggleAnnotationSelection(identifier) {
  selectedAnnotationIdentifiers.value = selectedAnnotationIdentifiers.value.includes(identifier)
    ? selectedAnnotationIdentifiers.value.filter((id) => id !== identifier)
    : [...selectedAnnotationIdentifiers.value, identifier];
}

function reveal3DBoxes() {
  if (!showTypes.value?.['3dbox']) {
    showTypes.value = { ...showTypes.value, '3dbox': true };
  }
  addVisualizationToLayout('3d-canvas');
}

function toggleAnnotationVisibility(identifier) {
  const wasHidden = hiddenAnnotationIdentifiers.value.includes(identifier);
  hiddenAnnotationIdentifiers.value = wasHidden
    ? hiddenAnnotationIdentifiers.value.filter((id) => id !== identifier)
    : [...hiddenAnnotationIdentifiers.value, identifier];
  const anno = currentSample.value?.annotations?.find((a) => a.id === identifier);
  if (wasHidden && anno && getAnnotationTypes(anno).includes('box3d')) {
    reveal3DBoxes();
  }
}

function toggleAnnotationTypeVisibility(identifier, typeKey) {
  const wasHidden = hiddenAnnotationIdentifiers.value.includes(annotationTypeHiddenKey(identifier, typeKey));
  toggleAnnotationVisibility(annotationTypeHiddenKey(identifier, typeKey));
  if (wasHidden && typeKey === '3dbox') {
    reveal3DBoxes();
  }
}

async function handleJoinFirst() {
  const identifiers = selectedAnnotationIdentifiers.value;
  console.log('[JoinFirst] selected identifiers:', identifiers);
  if (identifiers.length < 2) {
    return;
  }
  await joinAnnotationsInto(identifiers[0]);
}

async function handleJoinLast() {
  const identifiers = selectedAnnotationIdentifiers.value;
  if (identifiers.length < 2) {
    return;
  }
  await joinAnnotationsInto(identifiers[identifiers.length - 1]);
}

async function handleAnnotationReviewItemEdit(annotation) {
  const annotationIdentifier = Object.keys(annotationIdentifierMap.value).find((key) => annotationIdentifierMap.value[key] === annotation);
  selectedAnnotationIdentifiers.value = [annotationIdentifier];
  await nextTick();
  annotationCanvas.value.zoomToSelection();
}

async function handleSubmit() {
  // Editing through labelling task
  await handleLabellingSubmitAnnotations();
}

async function handleSequenceSubmit() {
  // Go to next frame (the cache only holds a sliding window, so its size is not the frame count)
  if (frame.value < frameCount.value) {
    await animationControls.value.handleSkipNext();
  }
}

function handleHoveredAnnotation(identifier) {
  hoveredAnnotationListIdentifier.value = identifier;
}

function handlePaneClick(event, pane) {
  activePane.value = pane;

  if (pane === 'canvas') {
    toolHotkeys.value = canvasHotkeys;
  } else if (pane === '3d-canvas') {
    toolHotkeys.value = canvas3DHotkeys;
  }

  if (pane === '3d-canvas' && ['3d-box', '3d-cluster'].includes(current3DCanvasTool.value)) {
    event.stopPropagation();
  }
}

function handleSelectedSequenceChanged(selectedSequence) {
  currentTaskImage.value = selectedSequence;
}

// Visualizations
const animationControls = ref(null);
const {
  keyframeAnnotations,
  topics,
  isInitializingAnimation,
  frame,
  frameCount,
} = storeToRefs(useViewerVisualizationsStore());
const showLayouts = ref(true);
if (auditorType.value === 'annotation') {
  showLayouts.value = false;
}
onUnmounted(() => {
  const { $reset } = useViewerVisualizationsStore();
  $reset();
});
watch(currentSample, () => {
  // if (currentSample.value) {
  //   setCurrentAnnotations([
  //     ...copyAnnotationsObject(currentSample.value.imageObj?.annotations),
  //     ...keyframeAnnotations.value,
  //   ]);
  // }
});
const isLoadingSample = computed(() => {
  if (currentSample.value) {
    return !currentSample.value.loaded;
  } else if (internalEditAsSequence.value || isSequence.value) {
    return true;
  }
  return false;
});
watch(() => currentSample.value.imageObj, () => {
  nextTick(() => {
    selectedAnnotationIdentifiers.value = [];
    if (hasSourceAnnotationSet.value) {
      if (currentSample.value?.imageObj?.review_status === "Done") {
        annotationSetToEdit.value = destinationAnnotationSet.value;
      } else {
        annotationSetToEdit.value = sourceAnnotationSet.value;
      }
    } else {
      annotationSetToEdit.value = destinationAnnotationSet.value;
    }
  });
});

// Setup all topics in sequence
async function getTopics() {
  const dataConnect = new DatastoreConnect();
  const topicsResp = await dataConnect.getDatasetTopics({ dataset_id: currentSample.value?.imageObj?.dataset_id });
  if (!topicsResp.result || topicsResp.error) {
    return null;
  }
  return topicsResp.result;
}

function handleAnnotationDeleteClicked(identifier) {
  setCurrentAnnotations([...currentSample.value.annotations.filter((anno) => anno.id !== identifier)]);
  sam2Annotations.value = [...sam2Annotations.value.filter((anno) => anno.id !== identifier)];
  aigt3dAnnotations.value = [...aigt3dAnnotations.value.filter((anno) => anno.id !== identifier)];
  aigt2dAnnotations.value = [...aigt2dAnnotations.value.filter((anno) => anno.id !== identifier)];
  selectedAnnotationIdentifiers.value = [];
}

const SIDEBAR_TYPE_TO_ANNOTATION_TYPE = { box: 'box', seg: 'segmentation', '3dbox': 'box3d' };

// Removes one geometry from an object; deletes the whole object when it was the last one.
function handleAnnotationTypeDelete(identifier, typeKey) {
  const target = currentSample.value.annotations.find((anno) => anno.id === identifier);
  if (!target) {
    return;
  }
  const removedType = SIDEBAR_TYPE_TO_ANNOTATION_TYPE[typeKey];
  if (getAnnotationTypes(target).every((type) => type === removedType)) {
    handleAnnotationDeleteClicked(identifier);
    return;
  }

  const updated = JSON.parse(JSON.stringify(target));
  if (removedType === 'box') {
    Object.assign(updated, {
      x: 0, y: 0, w: 0, h: 0,
    });
  } else if (removedType === 'segmentation') {
    delete updated.polygon;
  } else {
    delete updated.data_json;
  }
  updated.save_pending = true;
  updated.status = updated.status || 'updated';
  setCurrentAnnotations(currentSample.value.annotations.map((anno) => (anno.id === identifier ? updated : anno)));
}

const {
  handleAddVisualization: addVisualizationToLayout,
  handleVisualizationSelected: selectVisualizationInLayout,
  handleVisualizationContainerDrop,
  handleLayoutSelected,
  visualizationLocations,
  getLocationId,
  layout,
} = useVisualizationDisplays();

function enableGpsTopicVisibility() {
  if (!showTypes.value?.gps) {
    showTypes.value = {
      ...showTypes.value,
      gps: true,
    };
  }
}

function enable3DTopicsVisibility() {
  const currentShowTypes = showTypes.value || {};
  displayProjection.value = {
    ...displayProjection.value,
    '3dbox': true,
  };
  if (!currentShowTypes.pcd || !currentShowTypes.radar || !currentShowTypes.lidar || !currentShowTypes['3dbox'] || !currentShowTypes.imu) {
    showTypes.value = {
      ...currentShowTypes,
      pcd: true,
      radar: true,
      lidar: true,
      '3dbox': true,
      imu: true,
    };
  }
}

function enableDepthMapVisibility() {
  const currentShowTypes = showTypes.value || {};
  if (!currentShowTypes.depthmap) {
    showTypes.value = {
      ...currentShowTypes,
      depthmap: true,
    };
  }
}

function handleAddVisualization(visualization) {
  addVisualizationToLayout(visualization);
  if (visualization === 'location-map') {
    enableGpsTopicVisibility();
  }
  if (visualization === '3d-canvas') {
    enable3DTopicsVisibility();
  }
  if (visualization === 'depth-map') {
    enableDepthMapVisibility();
  }
}

function handleVisualizationSelected(event) {
  selectVisualizationInLayout(event);
  if (event?.visualization === 'location-map') {
    enableGpsTopicVisibility();
  }
  if (event?.visualization === '3d-canvas') {
    enable3DTopicsVisibility();
  }
  if (event?.visualization === 'depth-map') {
    enableDepthMapVisibility();
  }
}

async function downloadImage() {
  const url = `/v3/enterprise/image/${currentSample.value.imageObj.id}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = currentSample.value.imageObj.name || currentSample.value.imageObj.id || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading the image:', error);
  }
}

const pendingAnnotations = computed(() => {
  // sam2Annotations holds a single combined annotation per object (polygon + box2d + box3d)
  const combined = sam2Annotations.value.filter((anno) => anno.image_id === currentSample.value?.imageObj?.id);
  return {
    'seg': combined.filter((anno) => getAnnotationTypes(anno).includes('segmentation')),
    '3dbox': combined.filter((anno) => getAnnotationTypes(anno).includes('box3d')),
    '2dbox': combined.filter((anno) => getAnnotationTypes(anno).includes('box')),
  };
});

async function refreshAnnotationSets(dataset_id) {
  annotationSets.value = await getAnnotationSetsForDataset(dataset_id);
  annotationSets.value = [...annotationSets.value];
}

async function refreshLabels(dataset_id) {
  const refreshedLabels = await getLabelListForDataset(dataset_id);
  labels.value = refreshedLabels;
  labelColorMap.value = useAnnotationColorMap({ items: ref(refreshedLabels), key: 'index' });

  setDatasetLabels(refreshedLabels);

  return refreshedLabels;
}

async function getAnnotationSetsForDataset(dataset_id) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getAnnotationSets({
    dataset_id,
    get_num_annotations: true,
    get_num_images: true,
    get_labels: true,
  })
    .catch((error) => {
      console.log('Failed to retrieve sets:', error);
    });
  if (!resp || resp.error || !resp.result) {
    return [];
  }
  return resp.result;
}

async function getLabelListForDataset(dataset_id) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getLabelList({ dataset_id })
    .catch((error) => {
      console.log('Failed to retrieve labels:', error);
    });
  if (!resp || resp.error || !resp.result) {
    return [];
  }
  return resp.result;
}

async function handleStartEditingAnnotations(annotationSet) {
  annotationSetToEdit.value = annotationSet;
  reviewSettings.value = {
    reviewSessionMethod: "review_annotation_editor",
    batchSize: 10,
    samplingMethod: "random",
    reviewSession: null,
    reviewTask: {
      id: null,
      type: "audit",
      created_at: null,
      source_annotation_set_id: annotationSet.id,
      dest_annotation_set_id: annotationSet.id,
      image_id: parseInt(route.query.image_id),
      dataset_id: annotationSet.dataset_id,
      name: "",
      description: "",
      permission_type: "default",
      organization_id: 5,
      remainingCount: null,
    },
    initialAuditorType: "image",
  };
  selectedLabel.value = labels.value[0];
  mode.value = 'edit';
}

const combined = computed(() => ({
  isMounted: isMounted.value,
  imageObj: currentSample.value.imageObj,
  annotations: currentSample.value.annotations,
  showTypes: showTypes.value,
  // imagesParams: imagesParams.value,
  datasetFilters: datasetFilters.value,
  annotationDisplaySettings: annotationDisplaySettings.value,
  annotationSetToEditId: annotationSetToEdit.value?.id,
}));

watch(combined, () => {
  if (isMounted.value && route.name === 'datasets.gallery.viewer') {
    router.replace({
      path: route.path,
      query: {
        ...route.query,
        image_id: JSON.stringify(currentSample.value?.imageObj?.id),
        edit_annotation_set_id: annotationSetToEdit.value?.id,
        showTypes: JSON.stringify(showTypes.value),
        // imagesParams: JSON.stringify(this.imagesParams),
        datasetFilters: JSON.stringify(datasetFilters.value),
        annotationDisplaySettings: JSON.stringify(annotationDisplaySettings.value),
      },
    });
  }
}, { deep: true });

async function getReviewTask(reviewTaskId) {
  // this.loading = true;
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getReviewTaskById({ review_task_id: reviewTaskId })
    .catch((error) => {
      // TODO: handle error
      // this.loading = false;
      console.log(error);
    });
  if (resp.error || !resp.result) {
    // TODO: handle error
    // this.loading = false;
    return null;
  }
  // this.loading = false;
  return resp.result;
}

async function createReviewSession(reviewTaskId) {
  const dataConnect = new DatastoreConnect();
  const reviewSession = await dataConnect.addReviewSession({
    reviewer: store.state.user.user.username,
    duration: 0,
    review_task_id: reviewTaskId,
  })
    .then((resp) => {
      if (resp.error !== undefined) {
        throw Error(resp.error);
      }
      return resp;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });

  return reviewSession;
}

function openAddAnnotationSetsModal() {
  createAnnotationSetModal.value.showModal();
}

async function newAnnotationSetCreated() {
  if (sourceDatasetId.value) {
    // Setup image and annotations
    refreshAnnotationSets(sourceDatasetId.value)
      .then(() => {
        annotationDisplaySettings.value.displayAnnotationSets.annotation_sets = [annotationSets.value[0].id];
      });
  }
}

function openAddLabelsModal() {
  createLabelsModal.value.showModal();
}

async function newLabelsCreated() {
  if (sourceDatasetId.value) {
    // Setup image and annotations
    refreshLabels(sourceDatasetId.value)
      .then(() => {
        annotationDisplaySettings.value.displayLabels.labels = [...labels.value.map((l) => l.index)];
        if (labels.value.length > 0) {
          selectedLabel.value = labels.value[0];
        }
      });
  }
}

function setDatasetLabels(newLabels) {
  const currentDataset = store.state.datasets.currentDataset;
  const updatedDataset = {
    ...currentDataset,
    labels: newLabels,
  };
  store.commit('datasets/setCurrentDataset', updatedDataset);
  store.commit('datasets/updateDataset', updatedDataset);
}

async function updateLabelColor(params, obj) {
  params.dataset_id = obj.dataset_id;
  const dataConnect = new DatastoreConnect();
  await dataConnect.updateLabel(params)
    .then(async (data) => {
      if (!data.error) {
        await refreshLabels(obj.dataset_id);
      }
    })
    .catch((e) => console.log(e));
}

async function handleImageGroupChanged(group_id) {
  const oldGroupId = imageGroupId.value;
  currentSample.value.imageObj.group_id = group_id;

  const dataConnect = new DatastoreConnect();
  await dataConnect.setImageGroupId({ image_id: currentSample.value.imageObj.id, group_id })
    .then(async (data) => {
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to set group: ${data.error.message}`,
          type: 'error',
          position: 'top',
        });
        // Revert group_id due to error
        currentSample.value.imageObj.group_id = oldGroupId;
      }
    });
}
</script>

<style lang="scss" scoped>
.display {
  display: flex;
  flex-direction: row;
  position: relative;
  width: 100%;
  height: 100%;
  outline: none;
  overflow: hidden;
}

.visualization {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  position: relative;
  min-width: 0;

  &__horizontal-divider {
    outline: solid 2px gray;
    z-index: 2;
  }

  &__vertical-divider {
    min-width: 3px;
    max-width: 3px;
    background: var(--color-primary-400);
    z-index: 2;
  }
}

.annotation-editor-modal {
  &__sidebar {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 300px;
    min-width: 300px;
    padding: 0;
    z-index: 2;
    @include themify() {
      background: themed('color-white-100');
    }

    h3 {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 40px;
      padding: 4px 8px;
      margin: 0;
      text-align: left;
      box-shadow: 0px 1px 3px grey;
      @include themify() {
        background: themed('color-white-900');
      }
    }
  }
}

.annotation-tool {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  @include themify() {
    background: themed('image-background-fill');
  }
}

.annotation-review-sidebar {
  @include themify() {
    background: themed('color-white-100');
  }

  &__buttons {
    width: 100%;
    padding: 0 16px;

    button {
      width: 100%;
    }
  }

  &__annotations {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 300px;
    height: 100%;
    overflow-y: auto;
    scroll-snap-type: y mandatory;
  }

  &__snap-container {
    scroll-snap-align: center;
    min-height: calc(100% / 3);
    align-items: center;
    display: flex;
    flex-direction: column;
    width: 100%;
    justify-content: center;
    padding: 16px;

    &._active {
      background: rgb(0,0,0,0.125);
    }
  }
}

#no-image-remaining {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-primary);
}

.image-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;

  &__label {
    color: var(--body-text-color-secondary);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 8px 12px 16px;
}
:deep(.v-expansion-panel-title__overlay) {
  pointer-events: none;
}

</style>
